import React, { useMemo, useState } from 'react';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import styles from './Curiosidades.module.scss';

type Categoria = 'História' | 'Natureza' | 'Cultura';

interface Curiosidade {
  id: number;
  titulo: string;
  localizacao: string;
  categoria: Categoria;
  imagem: string;
  resumo: string;
  detalhes: string;
}

const curiosidades: Curiosidade[] = [
  {
    id: 1,
    titulo: 'São Cristóvão',
    localizacao: 'São Cristóvão',
    categoria: 'História',
    imagem: '/assets/curiosidades/sao-cristovao.jpg',
    resumo:
      'Fundada em 1590, São Cristóvão é uma das cidades mais antigas do Brasil.',
    detalhes:
      'São Cristóvão foi a primeira capital de Sergipe e preserva um importante conjunto arquitetônico colonial. A Praça São Francisco é reconhecida como Patrimônio Mundial pela UNESCO e representa um dos principais marcos históricos do estado.',
  },
  {
    id: 2,
    titulo: 'Cânion do Xingó',
    localizacao: 'Canindé de São Francisco',
    categoria: 'Natureza',
    imagem: '/assets/curiosidades/canion-xingo.jpg',
    resumo:
      'Os paredões rochosos do Cânion do Xingó foram formados ao longo de milhões de anos.',
    detalhes:
      'Localizado no Rio São Francisco, o Cânion do Xingó reúne águas esverdeadas e grandes paredões rochosos. O local pode ser visitado por meio de passeios de catamarã e é um dos destinos naturais mais conhecidos do interior sergipano.',
  },
  {
    id: 3,
    titulo: 'Grota do Angico',
    localizacao: 'Poço Redondo',
    categoria: 'História',
    imagem: '/assets/curiosidades/grota-do-angico.jpg',
    resumo:
      'O local ficou marcado por um dos acontecimentos mais conhecidos da história do cangaço.',
    detalhes:
      'A Grota do Angico ficou conhecida por ser o local onde Lampião, Maria Bonita e parte do grupo foram mortos em 1938. Atualmente, o destino recebe visitantes interessados na história do cangaço e nas paisagens da região.',
  },
  {
    id: 4,
    titulo: 'Croa do Goré',
    localizacao: 'Aracaju',
    categoria: 'Natureza',
    imagem: '/assets/curiosidades/croa-do-gore.jpg',
    resumo:
      'Um banco de areia que ganha destaque durante a maré baixa no Rio Vaza-Barris.',
    detalhes:
      'A Croa do Goré é um banco de areia cercado pelas águas do Rio Vaza-Barris. O local costuma ser visitado por meio de passeios de barco que saem da Orla do Pôr do Sol e oferece uma experiência diferente conforme a variação da maré.',
  },
  {
    id: 5,
    titulo: 'Aracaju planejada',
    localizacao: 'Aracaju',
    categoria: 'História',
    imagem: '/assets/curiosidades/aracaju-planejada.jpg',
    resumo:
      'A capital sergipana foi planejada antes mesmo de se tornar oficialmente a capital do estado.',
    detalhes:
      'Aracaju foi fundada em 1855 para substituir São Cristóvão como capital de Sergipe. Seu centro foi organizado com ruas distribuídas em formato semelhante a um tabuleiro, característica marcante do planejamento urbano da cidade.',
  },
  {
    id: 6,
    titulo: 'Museu da Gente Sergipana',
    localizacao: 'Aracaju',
    categoria: 'Cultura',
    imagem: '/assets/curiosidades/museu-gente-sergipana.jpg',
    resumo:
      'Um espaço interativo dedicado às tradições, aos costumes e à identidade do povo sergipano.',
    detalhes:
      'O Museu da Gente Sergipana apresenta manifestações culturais do estado de forma interativa. O visitante pode conhecer elementos da música, da culinária, das festas populares, do artesanato e das tradições locais.',
  },
  {
    id: 7,
    titulo: 'Mangue Seco',
    localizacao: 'Divisa entre Sergipe e Bahia',
    categoria: 'Natureza',
    imagem: '/assets/curiosidades/mangue-seco.jpg',
    resumo:
      'Dunas, coqueiros e paisagens marcantes transformaram Mangue Seco em um destino conhecido nacionalmente.',
    detalhes:
      'Mangue Seco reúne dunas, coqueirais e praias tranquilas próximas à divisa entre Sergipe e Bahia. O local ganhou projeção nacional após aparecer como cenário de produções audiovisuais e continua sendo visitado por turistas que partem do litoral sergipano.',
  },
  {
    id: 8,
    titulo: 'Mercados Centrais de Aracaju',
    localizacao: 'Aracaju',
    categoria: 'Cultura',
    imagem: '/assets/curiosidades/mercados-centrais-aracaju.jpg',
    resumo:
      'Artesanato, comidas típicas e produtos regionais reunidos em um dos pontos mais tradicionais da capital.',
    detalhes:
      'Os Mercados Centrais de Aracaju reúnem artesanato, bordados, doces regionais, castanhas, temperos, comidas típicas e lembranças da cultura sergipana. O local é uma boa opção para quem deseja conhecer produtos tradicionais do estado.',
  },
];

const curiosidadesRapidas = [
  'Sergipe é o menor estado brasileiro em extensão territorial.',
  'São Cristóvão foi a primeira capital de Sergipe.',
  'O Rio São Francisco forma parte da divisa entre Sergipe e Alagoas.',
  'O caranguejo é um dos símbolos mais conhecidos da identidade visual de Aracaju.',
  'A Orla de Atalaia possui uma extensa área de lazer, restaurantes e espaços culturais.',
];

const categorias = ['Todos', 'História', 'Natureza', 'Cultura'];

function Curiosidades() {
  const [categoriaAtiva, setCategoriaAtiva] = useState('Todos');
  const [curiosidadeSelecionada, setCuriosidadeSelecionada] =
    useState<Curiosidade | null>(null);

  const curiosidadesFiltradas = useMemo(() => {
    if (categoriaAtiva === 'Todos') {
      return curiosidades;
    }

    return curiosidades.filter(
      (curiosidade) => curiosidade.categoria === categoriaAtiva
    );
  }, [categoriaAtiva]);

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div
          className={styles.heroBackground}
          style={{
            backgroundImage:
              "url('/assets/curiosidades/banner-curiosidades-sergipe.jpg')",
          }}
        />
        <div className={styles.heroOverlay} />

        <div className={styles.heroContent}>
          <span className={styles.heroTag}>Explore Sergipe</span>
          <h1 className={styles.heroTitle}>Curiosidades de Sergipe</h1>
          <p className={styles.heroText}>
            Descubra histórias, paisagens e tradições que fazem parte da
            identidade sergipana.
          </p>
        </div>
      </section>

      <main className={styles.main}>
        <section className={styles.intro}>
          <div>
            <span className={styles.sectionTag}>Descubra novos lugares</span>
            <h2 className={styles.sectionTitle}>
              Histórias que tornam Sergipe único
            </h2>
          </div>

          <p className={styles.sectionDescription}>
            Explore fatos interessantes sobre pontos turísticos, cidades
            históricas e paisagens marcantes do estado.
          </p>
        </section>

        <section className={styles.filters}>
          {categorias.map((categoria) => (
            <button
              type="button"
              key={categoria}
              className={`${styles.filterButton} ${
                categoriaAtiva === categoria ? styles.filterActive : ''
              }`}
              onClick={() => setCategoriaAtiva(categoria)}
            >
              {categoria}
            </button>
          ))}
        </section>

        <section className={styles.cardsGrid}>
          {curiosidadesFiltradas.map((curiosidade) => (
            <article className={styles.card} key={curiosidade.id}>
              <div
                className={styles.cardImage}
                style={{ backgroundImage: `url(${curiosidade.imagem})` }}
              >
                <span className={styles.cardCategory}>
                  {curiosidade.categoria}
                </span>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.cardLocation}>
                  <LocationOnOutlinedIcon />
                  <span>{curiosidade.localizacao}</span>
                </div>

                <h3 className={styles.cardTitle}>{curiosidade.titulo}</h3>

                <p className={styles.cardText}>{curiosidade.resumo}</p>

                <button
                  type="button"
                  className={styles.cardButton}
                  onClick={() => setCuriosidadeSelecionada(curiosidade)}
                >
                  Saiba mais
                  <ArrowForwardRoundedIcon />
                </button>
              </div>
            </article>
          ))}
        </section>

        <section className={styles.quickFacts}>
          <div className={styles.quickFactsHeader}>
            <div className={styles.quickFactsIcon}>
              <LightbulbOutlinedIcon />
            </div>

            <div>
              <span className={styles.quickFactsTag}>Informações rápidas</span>
              <h2 className={styles.quickFactsTitle}>Você sabia?</h2>
            </div>
          </div>

          <div className={styles.quickFactsGrid}>
            {curiosidadesRapidas.map((curiosidade, index) => (
              <article className={styles.quickFactCard} key={curiosidade}>
                <span className={styles.quickFactNumber}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p>{curiosidade}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      {curiosidadeSelecionada && (
        <div
          className={styles.modalBackdrop}
          onClick={() => setCuriosidadeSelecionada(null)}
        >
          <section
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-label={`Curiosidade sobre ${curiosidadeSelecionada.titulo}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className={styles.modalImage}
              style={{
                backgroundImage: `url(${curiosidadeSelecionada.imagem})`,
              }}
            />

            <div className={styles.modalBody}>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setCuriosidadeSelecionada(null)}
                aria-label="Fechar modal"
              >
                <CloseRoundedIcon />
              </button>

              <span className={styles.modalCategory}>
                {curiosidadeSelecionada.categoria}
              </span>

              <h2 className={styles.modalTitle}>
                {curiosidadeSelecionada.titulo}
              </h2>

              <div className={styles.modalLocation}>
                <LocationOnOutlinedIcon />
                <span>{curiosidadeSelecionada.localizacao}</span>
              </div>

              <p className={styles.modalText}>
                {curiosidadeSelecionada.detalhes}
              </p>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default Curiosidades;